import jsPDF from "jspdf";
import "jspdf-autotable";
import autoTable from "jspdf-autotable";
import moment from "moment";
import AptosNormal from "../../../utils/Customfonts/Aptos.ttf";
import AptosBold from "../../../utils/Customfonts/Aptos-Bold.ttf";
import Stamp from "../../image/guidstamp.png";
import { addCustomFonts, loadAndAddFont } from "../../../utils/commonComponent/common";
import GeneralTermsPdfUrl from "./qtSample.pdf";
import { PDFDocument } from "pdf-lib";
const UnitechHeader = "../img/quotation_header.png";
const UnitechFooter = "../img/quotationFooter.png";

// header/footer letterhead images leave enough room for the body text.
let config = {
  margin: 40,
  borderMagin: 7,
  headerHeight: 85,
  footerHeight: 55,
  fontSize: 10,
  bordergap: 2,
};

// FONT FAMILY  –  declared first so every helper
export const customFontFamily = {
  bold:       "bold",
  normal:     "normal",
  boldFont:   "AptosBold",
  normalFont: "AptosNormal",
};

// Draws the letterhead header/footer images on the current page.
function addHeaderFooter(doc) {
  const pageWidth = doc.internal.pageSize.getWidth();
  const pageHeight = doc.internal.pageSize.getHeight();
  doc.addImage(UnitechHeader, "PNG", 0, 0, pageWidth, config.headerHeight);
  doc.addImage( UnitechFooter, "PNG", 0, pageHeight - config.footerHeight, pageWidth, config.footerHeight - 8);
}

// cursor. Otherwise returns currentY unchanged.
function ensureSpace(doc, currentY, neededHeight, letterHead) {
  const pageHeight = doc.internal.pageSize.getHeight();
  const bottomLimit = pageHeight - config.footerHeight + 10;
  if (currentY + neededHeight > bottomLimit) {
    doc.addPage();
    if (letterHead) addHeaderFooter(doc);
    return config.headerHeight + 25;
  }
  return currentY;
}

// Bold, underlined section title (e.g. "Subject:", "Scope of Work:").
function addSectionTitle(doc, title, x, y, letterHead) {
  y = ensureSpace(doc, y, config.fontSize + 8, letterHead);
  doc.setFont(customFontFamily.boldFont, customFontFamily.bold);  
  doc.setFontSize(config.fontSize + 0.5);
  doc.setTextColor(0, 0, 0);
  doc.text(title, x, y);
  const textWidth = doc.getTextWidth(title);
  doc.setLineWidth(0.4);
  doc.line(x, y + 2, x + textWidth, y + 2);
  return y + config.fontSize + 8;
}

// Wraps a single block of text (no manual line breaks expected) to maxWidth,
// page-breaking as needed. Returns the Y cursor after the text.
function addParagraph(doc, text, x, y, maxWidth, letterHead, bold = false) {
  if (!text) return y;
  if (bold){
    doc.setFont(customFontFamily.boldFont, customFontFamily.bold);
  } else {
    doc.setFont(customFontFamily.normalFont, customFontFamily.normal);
  }
  doc.setFontSize(config.fontSize);
  doc.setTextColor(0, 0, 0);
  const lines = doc.splitTextToSize(String(text), maxWidth);
  const lineHeight = config.fontSize * 1.3;
  let cursorY = y;
  lines.forEach((line) => {
    cursorY = ensureSpace(doc, cursorY, lineHeight, letterHead);
    doc.text(line, x, cursorY);
    cursorY += lineHeight;
  });
  return cursorY + 4;
}

// Like addParagraph but preserves the author's own line breaks (from a
// textarea) — each line is wrapped independently instead of being merged
// into one flowing paragraph. Optionally bullets each non-empty line.
function addMultilineParagraph(doc, text, x, y, maxWidth, letterHead, bulleted = false) {
  if (!text) return y;
  doc.setFont(customFontFamily.normalFont, customFontFamily.normal);
  doc.setFontSize(config.fontSize);
  doc.setTextColor(0, 0, 0);
  const lineHeight = config.fontSize * 1.3;
  const rawLines = String(text).split("\n");
  let cursorY = y;
  rawLines.forEach((raw) => {
    const trimmed = raw.trim();
    if (!trimmed) {
      cursorY += lineHeight * 0.5;
      return;
    }
    // show dot like li tag
    // const prefix = bulleted ? "\u2022  " : "";
    const prefix = bulleted ? "  " : "";
    const wrapped = doc.splitTextToSize(prefix + trimmed, maxWidth);
    wrapped.forEach((line) => {
      cursorY = ensureSpace(doc, cursorY, lineHeight, letterHead);
      doc.text(line, x, cursorY);
      cursorY += lineHeight;
    });
  });
  return cursorY + 4;
}

// as the primary recipient, the rest as CC.
function formatContactList(str, showToCC = true) {
  const items = (str || "").split(",").map((s) => s.trim()).filter(Boolean);
  if (items.length === 0) return "";
  return items.map((item, idx) => showToCC ? `${idx === 0 ? "To" : "CC"}: ${item}` : item).join("\n");
}

function drawTitleBlock(doc, inputStates, letterHead) {
  const y0 = config.headerHeight + 25;
  let y = y0;
  doc.setFont(customFontFamily.boldFont, customFontFamily.bold);
  doc.setFontSize(config.fontSize);
  const revNum = inputStates?.quotationNumber || "";
  const dateStr = inputStates?.date ? moment(inputStates.date).format("DD/MM/YYYY") : "";
  doc.text(`Number: ${revNum}${dateStr ? `, Dated-${dateStr}` : ""}`, config.margin, y);
  return y + 10;
}

// Splits a single logical line into its wrapped physical lines at the given
// width, using whichever font is currently set on `doc` (caller sets bold
// vs normal before calling, so wrapping matches the font that will render).
function wrapField(doc, text, maxWidth) {
  if (!text) return [];
  return doc.splitTextToSize(String(text), maxWidth);
}

function drawToFromBox(doc, inputStates, signer, startY) {
  const pageWidth = doc.internal.pageSize.getWidth();
  const colWidth = (pageWidth - config.margin * 2) / 2;
  const cellPadding = 6;
  const innerWidth = colWidth - cellPadding * 2 - 1;
  const borderWidth = 0.75;

  doc.setFontSize(config.fontSize - 1);

  const toFieldDefs = [
    { text: "To,", bold: false },
    { text: inputStates?.contact || "", bold: true },
    { text: inputStates?.contactDept || "", bold: false },
    { text: inputStates?.companyName || "", bold: true },
    { text: inputStates?.address || "", bold: false },
    { text: inputStates?.contactNumber ? `Mobile No.: ${inputStates.contactNumber}` : "", bold: false },
    { text: inputStates?.emailId ? `Email ID: ${inputStates.emailId}` : "", bold: false },
  ];
  const toLines = [];
  toFieldDefs.forEach(({ text, bold }) => {
    if (!text) return;
    doc.setFont(
      bold ? customFontFamily.boldFont : customFontFamily.normalFont,
      bold ? customFontFamily.bold : customFontFamily.normal
    );
    wrapField(doc, text, innerWidth).forEach((line) => toLines.push({ text: line, bold }));
  });

  const fromFieldDefs = [
    { text: "From", bold: false },
    { text: signer?.name || "", bold: true },
    { text: "DYC Global Private Limited", bold: true },
    { text: "B-702, Lodha Supremus, Kolshet,", bold: false },
    { text: "Thane (West), Thane \u2013 400607,", bold: false },
    { text: "Maharashtra, India.", bold: false },
    { text: "Mobile No.: +91 9284011025", bold: false },
    { text: "Landline No.: +91 2249628259", bold: false },
    { text: "Email Id.: " + (signer.email || ""), bold: false },
  ];
  const fromLines = [];
  fromFieldDefs.forEach(({ text, bold }) => {
    doc.setFont(
      bold ? customFontFamily.boldFont : customFontFamily.normalFont,
      bold ? customFontFamily.bold : customFontFamily.normal
    );
    wrapField(doc, text, innerWidth).forEach((line) => fromLines.push({ text: line, bold }));
  });

  // Same textual content as before — just pre-wrapped here so autoTable's
  const toContent = toLines.map((l) => l.text).join("\n");
  const fromContent = fromLines.map((l) => l.text).join("\n");

  autoTable(doc, {
    startY,
    theme: "grid",
    body: [[{ content: toContent }, { content: fromContent }]],
    styles: {
      fontSize: config.fontSize - 1,
      cellPadding: cellPadding,
      textColor: 0,
      lineWidth: borderWidth,
      lineColor: [0, 0, 0],
      valign: "top",
      font: customFontFamily.normalFont,
      fontStyle: customFontFamily.normal,
    },
    columnStyles: {
      0: { cellWidth: colWidth },
      1: { cellWidth: colWidth },
    },
    margin: { left: config.margin, right: config.margin },
    didDrawCell: (data) => {
      if (data.section !== "body") return;
      const cell = data.cell;
      const lines = data.column.index === 0 ? toLines : fromLines;
      // Cover the plain-font text autoTable just drew (border untouched),
      // then redraw the same lines with the correct bold/normal font.
      doc.setFillColor(255, 255, 255);
      doc.rect(cell.x + 1, cell.y + 1, cell.width - 2, cell.height - 2, "F");

      const lineHeight = (config.fontSize - 1) * 1.15;
      let ly = cell.y + cellPadding + (config.fontSize - 1) * 0.85;
      lines.forEach((line) => {
        doc.setFont(
          line.bold ? customFontFamily.boldFont : customFontFamily.normalFont,
          line.bold ? customFontFamily.bold : customFontFamily.normal
        );
        doc.setFontSize(config.fontSize - 1);
        doc.text(line.text, cell.x + cellPadding, ly);
        ly += lineHeight;
      });
      // Redraw the border on top, since it sits at the cell edge and could
      doc.setDrawColor(0, 0, 0);
      doc.setLineWidth(borderWidth);
      doc.rect(cell.x, cell.y, cell.width, cell.height);
    },
  });

  return doc.lastAutoTable.finalY + 18;
}

export async function generatePDFQuotation(
  inputStates,
  letterHead,
  productDetails,
  quotationObject,
  setDownloadPdfloading,
  signer
) {
  try {
    setDownloadPdfloading(true);
    const doc = new jsPDF({
      orientation: "p",
      unit: "pt",
      format: "a4",
      compress: true,
    });
    await Promise.all([
      loadAndAddFont(doc, AptosNormal, "Aptos", customFontFamily.normalFont, customFontFamily.normal),
      loadAndAddFont(doc, AptosBold, "Aptos-Bold", customFontFamily.boldFont, customFontFamily.bold),
    ]);
    doc.setFont(customFontFamily.normalFont, customFontFamily.normal);

    const pageWidth = doc.internal.pageSize.getWidth();
    const contentWidth = pageWidth - config.margin * 2;

    if (letterHead) addHeaderFooter(doc);

    let y = drawTitleBlock(doc, inputStates, letterHead);
    y = drawToFromBox(doc, inputStates, signer, y);

    // Subject
    y = addSectionTitle(doc, "Subject:", config.margin, y, letterHead);
    y = addParagraph(doc, inputStates?.subject, config.margin, y, contentWidth, letterHead);

    // References
    y = addSectionTitle(doc, "References:", config.margin, y, letterHead);
    y = addParagraph(doc, inputStates?.enquiryReference, config.margin, y, contentWidth, letterHead);

    // Salutation + static intro
    y = addParagraph(doc, "Dear Sir,", config.margin, y, contentWidth, letterHead);
    y = addParagraph(doc, "We acknowledge receipt of your aforementioned enquiry with gratitude. Your expressed interest in the services offered by DYC Global Private Limited is greatly appreciated.", config.margin, y, contentWidth, letterHead);
    y = addParagraph(doc, "We are delighted to present our proposal as outlined below:", config.margin, y, contentWidth, letterHead);

    // Scope of Work
    y = addSectionTitle(doc, "Scope of Work:", config.margin, y, letterHead);
    y = addMultilineParagraph(doc, inputStates?.scopeWork, config.margin, y, contentWidth, letterHead);

    // Deliverable
    y = addSectionTitle(doc, "Deliverable:", config.margin, y, letterHead);
    y = addMultilineParagraph(doc, inputStates?.deliverables, config.margin, y, contentWidth, letterHead, true);

    // Static boilerplate paragraph (no matching input field currently)
    y = addParagraph(doc, "The Management/Project Team of the client shall inform the Coordinator at DYC Global Private Limited at least 3 working days in advance regarding the inspection activities to be carried out at the vendor location.", config.margin, y, contentWidth, letterHead, true);

    // Communication Protocol
    y = addSectionTitle(doc, "Communication Protocol:", config.margin, y, letterHead);
    y = addMultilineParagraph(doc, inputStates?.communicationProtocol, config.margin, y, contentWidth, letterHead);

    // Coordination (intro) + Inspection Services breakdown
    y = addSectionTitle(doc, "Coordination:", config.margin, y, letterHead);
    y = addMultilineParagraph(doc, inputStates?.Coordination, config.margin, y, contentWidth, letterHead);

    y = addParagraph(doc, "Inspection Services:", config.margin, y, contentWidth, letterHead, true);

    const inspectionText = formatContactList(inputStates?.inspection);
    if (inspectionText) {
      y = addParagraph(doc, "Inspection:", config.margin, y, contentWidth, letterHead, true);
      y = addMultilineParagraph(doc, inspectionText, config.margin + 15, y, contentWidth - 15, letterHead);
    }

    const commercialsText = formatContactList(inputStates?.commercialsContactPerson);
    if (commercialsText) {
      y = addParagraph(doc, "Commercials:", config.margin, y, contentWidth, letterHead, true);
      y = addMultilineParagraph(doc, commercialsText, config.margin + 15, y, contentWidth - 15, letterHead);
    }
    
    const TechnicalContactText = formatContactList(inputStates?.TechnicalContact, false);
    if (TechnicalContactText) {
      y = addParagraph(doc, "Technical:", config.margin, y, contentWidth, letterHead, true);
      y = addMultilineParagraph(doc, TechnicalContactText, config.margin + 15, y, contentWidth - 15, letterHead);
    }

    // Professional Fees
    y = addSectionTitle(doc, "Professional Fees:", config.margin, y, letterHead);
    y = addMultilineParagraph(doc, inputStates?.professionalfees, config.margin, y, contentWidth, letterHead);

    // Payment Terms
    y = addSectionTitle(doc, "Payment Terms:", config.margin, y, letterHead);
    y = addMultilineParagraph(doc, inputStates?.paymentTerms, config.margin, y, contentWidth, letterHead);

    // Validity
    y = addSectionTitle(doc, "Validity:", config.margin, y, letterHead);
    y = addMultilineParagraph(doc, inputStates?.quotationValidity, config.margin, y, contentWidth, letterHead);

    // Terms & Conditions
    y = addSectionTitle(doc, "Terms & Conditions:", config.margin, y, letterHead);
    y = addMultilineParagraph(doc, inputStates?.termsConditions, config.margin, y, contentWidth, letterHead);

    y = addParagraph(doc, "Sincerely Yours,", config.margin, y, contentWidth, letterHead);
    y = addParagraph(doc, "For DYC Global Private Limited", config.margin, y, contentWidth, letterHead, true);

    // Signature stamp overlay
    if(signer.signatureUrl){
      doc.addImage(signer.signatureUrl, "PNG", config.margin, y, 100, 50);
    }
    y += 55;
    y = addParagraph(doc, signer.name || "", config.margin, y, contentWidth, letterHead, true);

    // Attachments / revision info
    y = addSectionTitle(doc, "Attachment(s):", config.margin, y, letterHead);
    y = addParagraph(doc, "DYC General terms & Conditions - DYC/FMT-MR/29 Rev. 00 Revision date: 24.05.2023", config.margin, y, contentWidth, letterHead);
    y = addParagraph(doc, `Revision Number [${String(quotationObject?.revisionNumber ?? 0).padStart(2, "0")}]: -`, config.margin, y, contentWidth, letterHead);
    y = addParagraph(doc, "Reason for Revision: -", config.margin, y, contentWidth, letterHead);
    y = addParagraph(doc, "This Document Supersedes Quotation No.: -", config.margin, y, contentWidth, letterHead);

    // ---- Page 3: Acceptance of Quotation (always its own page) ----
    doc.addPage();
    if (letterHead) addHeaderFooter(doc);
    let ay = config.headerHeight + 30;

    ay = addSectionTitle(doc, "Acceptance of Quotation:", config.margin, ay, letterHead);
    ay = addParagraph(doc, `I/We confirm the acceptance of DYC Global's Quotation No. ${inputStates?.quotationNumber || ""}${
        inputStates?.date ? ` Dated ${moment(inputStates.date).format("DD/MM/YYYY")}` : ""
      }`, config.margin, ay, contentWidth, letterHead);
    ay += 20;
    doc.setFont(customFontFamily.normalFont, customFontFamily.normal);
    doc.setFontSize(config.fontSize);
    doc.text("Client signature & Company's seal_____________________________", config.margin, ay);
    ay += 30;
    doc.text("Date ________________", config.margin, ay);
    ay += 30;
    doc.text("Name / Designation: _________________________", config.margin, ay);

    // ---- Page numbers (bottom right, all pages) ----
    const totalPages = doc.getNumberOfPages();
    for (let page = 1; page <= totalPages; page++) {
      doc.setPage(page);
      doc.setFont(customFontFamily.boldFont, customFontFamily.bold);
      doc.setFontSize(config.fontSize - 1);
      doc.setTextColor(0);
      const pw = doc.internal.pageSize.getWidth();
      const ph = doc.internal.pageSize.getHeight();
      doc.text(`Page No : ${String(page).padStart(2, "0")} of ${String(totalPages).padStart(2, "0")}`, pw - config.margin - 60, ph - 12);
    }

    // ---- Merge in the actual General Terms & Conditions PDF, then download ----
    const quotationBytes = doc.output("arraybuffer");
    const mergedBytes = await appendGeneralTermsPdf(quotationBytes, GeneralTermsPdfUrl);
    const blob = new Blob([mergedBytes], { type: "application/pdf" });
    const blobUrl = URL.createObjectURL(blob);
    const link = document.createElement("a");
    link.href = blobUrl;
    link.download = `Quotation_${inputStates?.quotationNumber || "draft"}.pdf`;
    document.body.appendChild(link);
    link.click();
    document.body.removeChild(link);
    URL.revokeObjectURL(blobUrl);
    setDownloadPdfloading(false);
  } catch (error) {
    setDownloadPdfloading(false);
    console.log("pdf generating error", error);
  }
}


// the merged document as bytes ready to be downloaded.
async function appendGeneralTermsPdf(quotationPdfBytes, attachmentUrl) {
  const mainPdf = await PDFDocument.load(quotationPdfBytes);
  const attachmentBytes = await fetch(attachmentUrl).then((res) => {
    if (!res.ok) throw new Error(`Could not fetch attachment PDF: ${res.status}`);
    return res.arrayBuffer();
  });
  const attachmentPdf = await PDFDocument.load(attachmentBytes);
  const copiedPages = await mainPdf.copyPages(attachmentPdf, attachmentPdf.getPageIndices());
  copiedPages.forEach((page) => mainPdf.addPage(page));
  return mainPdf.save(); // Uint8Array of the merged PDF
}

// Appends the static "General Terms and Conditions" document as additional
function drawGeneralTermsAndConditions(doc, letterHead, contentWidth) {
  doc.addPage();
  if (letterHead) addHeaderFooter(doc);
  let y = config.headerHeight + 25;

  doc.setFont(customFontFamily.boldFont, customFontFamily.bold);
  doc.setFontSize(config.fontSize + 2);
  doc.setTextColor(0, 0, 0);
  doc.text("General Terms and Conditions of DYC Global Pvt. Ltd.", config.margin, y);
  y += 20;

  const clauses = [
    {
      title: "1. Introduction",
      text: "DYC Global Private Limited, established in India in October 2022, specializes in delivering expert services, particularly in the domain of inspection services as outlined in the Company's Memorandum of Association.",
    },
    {
      title: "2. Contractual Parameters",
      text: "The validity and provisions of agreements formalized by DYC Global are contingent upon the issuance of a corresponding written order confirmation. Offers regarding performance, pricing, and timelines lack binding status in the absence of this confirmation. The amalgamation of these General Terms and Conditions, in conjunction with the conditions delineated in the DYC Global offer letter and any associated modifications, forms the fundamental structure of the contractual arrangement. External terms, conditions, or General Terms & Conditions, regardless of alignment with DYC Global's stance, do not assimilate into the contract. DYC Global's service delivery strictly adheres to relevant standards, codes, and the defined scope specified in the contract/QAP/ITP. The company relinquishes responsibility for validating or ensuring the accuracy of safety regulations and programs, pivotal to its inspections and evaluations, unless explicitly sanctioned in writing. Any modifications to these Terms and Conditions require written acknowledgment by an authorized representative of DYC Global to be legally binding. In case of conflict, these Terms and Conditions supersede any document imposing contradictory terms. DYC Global communicates inspection outcomes through proprietary formats such as Inspection Reports, Inspection Release Notes/Certificates, or client-specific formats to designated interfaces at stipulated intervals.",
    },
    {
      title: "3. Client Assistance",
      text: "Clients are obliged to extend necessary support, at no cost, for the effective execution of the contract, encompassing efforts from their own personnel and any other engaged parties. This support includes facilitating access to documented inspection history, ensuring secure site entry, providing timely access to essential interfaces, preparing items for inspection in a timely manner, and authorizing essential audits on DYC Global as needed for accreditation and approval processes by regulatory, certification, and accreditation entities. Compliance with pertinent standards, safety regulations, and accident prevention protocols is imperative. Unauthorized access to inspected items and related equipment/facilities must be prevented. DYC Global reserves the right to verify the ongoing suitability of inspected items and equipment/facilities for their intended purpose through visual assessments, functional evaluations, supplementary tests if required, examination of records, or recalibration of equipment/facilities. Additional costs incurred due to delayed or inadequate assistance by the client are their responsibility. Charges may be levied by DYC Global for such costs even if a fixed or maximum price has been pre-established. The client is accountable for expenses arising from rework or delays resulting from delayed, inaccurate, or incomplete information, adverse weather conditions, or issues attributed to vendors. Ensuring a secure work environment for DYC Global's inspectors is the client's responsibility, and inspection activities may be withheld until unsafe conditions are rectified and deemed safe.",
    },
    {
      title: "4. Liability Constraints",
      text: "In the delivery of services, provision of information, or dispensing advice, DYC Global disclaims any warranties regarding the precision or authenticity of disseminated information, advice, or signed reports unless explicitly confirmed in writing. The company, its engineers, or representatives bear no liability for losses, damages, or expenses arising from services or advice unless there is demonstrated willful misconduct or gross negligence. The liability of DYC Global shall not exceed the pro rata fees paid or payable for specific services or advice that led to the damage, loss, or expense. The aggregate liability of DYC Global under this contract, whether contractual or tortious, shall not surpass 20% of the received contract price. DYC Global and its affiliates, associates, or employees are not liable to any party not directly party to the specified service contract. The issuance of inspection reports, scrutiny of Test Certificates/Reports, or the issuance of Inspection Release Notes/Certificates does not exempt the Client/End User/Supplier/Manufacturer from their obligation to provide/utilize authentic materials/items and documents in compliance with applicable Orders, Specifications, Technical, Quality, Quantity, Warranty, Guarantee, statutory, and regulatory requisites. The authenticity of supplied materials/items and submitted documents is the exclusive legal liability of the Supplier/Manufacturer/Stockiest. Non-performance or non-observance of any stipulation, condition, or obligation mandated in this Contract does not constitute grounds for claims against DYC Global or its employees/associates, provided such lapses arise from factors beyond the entity's reasonable control. DYC Global, its engineers, and representatives shall not be held liable for indirect consequential losses, damages, or expenses, including losses in use, production, or profits.",
    },
    {
      title: "5. Customer Complaints",
      text: "Clients may raise complaints regarding service quality, delays, or other concerns through written communication to DYC Global. Upon receipt, complaints are promptly logged and acknowledged within 48 hours. The Technical Manager will oversee a thorough investigation, including root cause analysis, and corrective actions will be implemented. A preliminary report outlining the steps taken will be provided to the client within 7 days, with ongoing updates as necessary. DYC Global aims to resolve complaints within 15 working days. If unresolved, clients may escalate the issue to the Technical Manager for further review or proceed with the arbitration process as outlined in Section 11.",
    },
    {
      title: "6. Billing and Remuneration",
      text: "Unless explicitly specified in writing by DYC Global, all fees, charges, and expenses payable must be settled within 7 days of invoicing. In cases of contested invoices, undisputed portions must be promptly paid within 7 days. Disputes regarding invoices or segments thereof must be formally communicated in writing within 7 days from the date of invoice receipt. DYC Global reserves the right to levy 2% interest per month on outstanding amounts in case of delayed payments. If a fixed pricing structure has been established in writing, DYC Global is entitled to invoice and receive payment for pro rata installments based on services rendered. If either party terminates the contract before fulfillment of services, fees shall be assessed on a pro rata basis up to the termination date. Any reasonable costs associated with premature contract termination, along with outstanding dues, shall be immediately payable. DYC Global may withhold or withdraw certificates or reports in the event of non-payment of fees.",
    },
    {
      title: "7. Force Majeure",
      text: 'The term "Force Majeure" covers conditions hindering DYC Global\'s service delivery, including a range of scenarios from natural disasters to governmental acts. DYC Global remains entitled to remuneration for periods impacted by Force Majeure factors. The service timeline will be extended to cover the duration of the Force Majeure event, along with necessary allowances for remobilization if required. If a Force Majeure circumstance persists uninterrupted for 2 months or more, DYC Global reserves the right to unilaterally terminate the Contract, effective immediately, upon written notice to the Client. In such an event, the Client is liable to remunerate DYC Global for all outstanding sums owed up until the termination date under the Contract, in addition to stipulated demobilization charges.',
    },
    {
      title: "8. Temporal Limitations",
      text: "This Contract remains operative until the specified expiry date or termination, with either DYC Global or the Client possessing the right to terminate upon 30 days' notice. Claims against DYC Global expire after 6 months from the date of service provision.",
    },
    {
      title: "9. Transfer of Rights and Obligations",
      text: "Transfer of rights and obligations arising from this Contract, whether in whole or part, requires express written consent from both the client and DYC Global.",
    },
    {
      title: "10. Intellectual Property and Confidentiality",
      text: "Intellectual property rights exclusively belong to DYC Global, and reproduction of materials requires prior consent. Both parties and their associates are obligated to maintain confidentiality of designated information and documents unless required by law or client directive. This confidentiality obligation remains for 3 years post the contract's conclusion or termination, excluding non-confidential or publicly available information and independently sourced information by DYC Global. Inspection records may be shared as mandated. Breaches of confidentiality by DYC Global are subject to the liability clause.",
    },
    {
      title: "11. Arbitration",
      text: "Disputes arising from this contract shall be referred to arbitration as per the Indian Arbitration and Conciliation Act, 1996. The jurisdictional seat is Mumbai, and the governing law is Indian. Arbitration decisions are binding.",
    },
    {
      title: "12. Timeframes",
      text: "Timelines provided by DYC Global are estimates and become binding only when confirmed in writing after specifications are presented.",
    },
    {
      title: "13. Non-Solicitation",
      text: "Clients are prohibited from offering employment or engagement to DYC Global personnel during the contract and for 2 years after its conclusion.",
    },
  ];

  clauses.forEach(({ title, text }) => {
    y = addSectionTitle(doc, title, config.margin, y, letterHead);
    y = addParagraph(doc, text, config.margin, y, contentWidth, letterHead);
  });
}